/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import {
  useDeleteArticleContentMutation,
  useFetchVersionsListQuery,
  useRestoreArticleContentMutation
} from '@services/versionHistoryService';
// import { getRevision } from '../../../../api/revisions'
import { useApplicationState } from '../../../../hooks/common/use-application-state';
import type { ModalVisibilityProps } from '../../../common/modals/common-modal';
import { useUiNotifications } from '../../../notifications/ui-notification-boundary';
// import { downloadRevision } from './utils';
import React, { useCallback, useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  DropdownButton,
  Modal
} from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Loader } from 'react-bootstrap-typeahead';
import { VersionMetadata } from '@api/revisions/types';
import { getFormattedTime } from './utils';
import ConfirmationModal from '@components/common/modals/confirmation-modal';
import { VersionIndexType } from './revision-modal';
import { UiIcon } from '@components/common/icons/ui-icon';
import { ArrowRight } from 'react-bootstrap-icons';
import { useRevalidateArticle } from '@hooks/common/use-revalidate-article';
import { setEditorDocChange } from '@redux/editor/methods';

export interface RevisionModalFooterProps {
  selectedVersionIndex: VersionIndexType | undefined;
  onVersionSelect: Function;
  versionContentTrigger: Function;
  setSelectedVersionIndex: Function;
}

/**
 * Renders the footer of the revision modal that includes buttons to download the currently selected revision or to
 * revert the note content back to that revision.
 *
 * @param selectedVersionIndex The currently selected revision id or undefined if no revision was selected.
 * @param onHide Callback that is fired when the modal is about to be closed.
 */
export const RevisionModalFooter: React.FC<
  RevisionModalFooterProps & Pick<ModalVisibilityProps, 'onHide'>
> = ({
  selectedVersionIndex,
  onHide,
  onVersionSelect,
  versionContentTrigger,
  setSelectedVersionIndex
}) => {
  const { t } = useTranslation();
  const { dispatchUiNotification } =
    useUiNotifications();
  const entitySlug = useApplicationState(
    (state) => state.noteDetails.aliases[0].noteId
  );
  const { revalidateArticleCallback } = useRevalidateArticle(['', entitySlug]); // '' empty to revalidate home page

  const [showConfirmation, setShowConfirmation] = useState<{
    show: boolean;
    message: string | JSX.Element;
    action: 'restore' | 'delete' | '';
  }>({
    show: false,
    message: '',
    action: ''
  });
  const {
    currentData: versionsListData,
    isFetching: versionsListIsFetching,
    isError: versionsListIsError
  } = useFetchVersionsListQuery({ entitySlug });
  const [restoreVersion] = useRestoreArticleContentMutation();
  const [deleteVersion] = useDeleteArticleContentMutation();

  const onRestoreToVersion = useCallback(() => {
    if (selectedVersionIndex === undefined) {
      dispatchUiNotification('Version Restore Failed',t('editor.modal.revision.errorVersionId'), {});
      return;
    }
    restoreVersion({
      entitySlug,
      versionId:
        versionsListData?.versions[selectedVersionIndex.newVerIndex!].attributes
          .version_id
    })
      .unwrap()
      .then((res) => {
        if (res.success) {
          dispatchUiNotification(
            'Version Restore Success',
            'Version Restored Successfully',
            {}
          );
          setEditorDocChange(false);
          revalidateArticleCallback();
        } else {
          dispatchUiNotification('Version Restore Failed',res.error, {});
        }
        setShowConfirmation({
          show: false,
          message: '',
          action: ''
        });
        setSelectedVersionIndex();
      })
      .catch((err) => {
        dispatchUiNotification('Version Restore Failed',err.data.error, {});
        setShowConfirmation({
          show: false,
          message: '',
          action: ''
        });
      });
  }, [
    entitySlug,
    restoreVersion,
    selectedVersionIndex,
    versionsListData,
    onHide,
    t,
    dispatchUiNotification
  ]);

  // const onDownloadRevision = useCallback(() => {
  //   if (selectedRevisionId === undefined) {
  //     return
  //   }
  //   getRevision(noteIdentifier, selectedRevisionId)
  //     .then((revision) => {
  //       downloadRevision(noteIdentifier, revision)
  //     })
  //     .catch(showErrorNotification(''))
  // }, [noteIdentifier, selectedRevisionId, showErrorNotification])

  const handleOldVerSelect = useCallback(
    (index: number) => {
      if (selectedVersionIndex === undefined) {
        dispatchUiNotification('Failed to fetch version',t('editor.modal.revision.errorVersionId'), {});
        return;
      }
      const allVer = versionsListData?.versions;
      const versions: string[] = [
        allVer[selectedVersionIndex.newVerIndex!].attributes.version_id, // new
        allVer[index].attributes.version_id // old
      ];
      versionContentTrigger(
        {
          entitySlug,
          versionID: versions // [newVer, oldVer]
        },
        true
      );

      return onVersionSelect({
        newVerIndex: selectedVersionIndex.newVerIndex,
        oldVerIndex: index,
        verDetails: {
          old: {
            date: `${getFormattedTime(
              allVer[index].attributes.created_at,
              'DD TT'
            )} ${index === 0 ? '(Current Version)' : ''}}`,
            userData: allVer[index].attributes.user_data
          },
          new: {
            date: `${getFormattedTime(
              allVer[selectedVersionIndex.newVerIndex!].attributes.created_at,
              'DD TT'
            )} ${
              selectedVersionIndex.newVerIndex! === 0 ? '(Current Version)' : ''
            }`,
            userData:
              allVer[selectedVersionIndex.newVerIndex!].attributes.user_data
          }
        }
      });
    },
    [
      entitySlug,
      onVersionSelect,
      selectedVersionIndex,
      versionContentTrigger,
      versionsListData,
      t
    ]
  );

  const getConfirmationModalMessageUI = (action: 'restore' | 'delete') => {
    const allVer = versionsListData?.versions;
    const selectedVerDate = getFormattedTime(
      allVer[selectedVersionIndex.newVerIndex!].attributes.created_at,
      'DDDD TT'
    );
    const selectedVerAuthor =
      allVer[selectedVersionIndex.newVerIndex!].attributes.user_data.name;
    const selectedVerEmail =
      allVer[selectedVersionIndex.newVerIndex!].attributes.user_data.email;

    const currentVerDate = getFormattedTime(
      allVer[0].attributes.created_at,
      'DDDD TT'
    );
    const currentVerAuthor = allVer[0].attributes.user_data.name;
    const currentVerEmail = allVer[0].attributes.user_data.email;

    const isDelete = action === 'delete';
    const GetDetailUi = ({ date, author, email }: {
      date: string;
      author: string;
      email: string;
    }) => (
      <>
        <p className='m-0'>Version: {date}</p>
        <p className='m-0'>Created by: {author}</p>
        <p className='m-0'>Email: {email}</p>
      </>
    );

    return (
      <div className='container'>
        <h2 className='mb-5'>
          {isDelete
            ? t('editor.modal.revision.deleteMessage')
            : t('editor.modal.revision.restoreMessage')}
        </h2>
        <div className='row text-info'>
          {!isDelete ? (
            <div className='col'>
              <>
                <h3>
                  <u className='text-secondary'>Current Version:</u>
                </h3>
                <GetDetailUi
                  date={currentVerDate}
                  author={currentVerAuthor}
                  email={currentVerEmail}
                />
              </>
            </div>
          ) : null}
          {!isDelete ? (
            <div className='col-1 p-0 d-flex justify-content-center align-items-center'>
              <UiIcon className='text-secondary' icon={ArrowRight} />
            </div>
          ) : null}
          <div className='col'>
            {!isDelete ? (
              <>
                <h3>
                  <u className='text-secondary'>Version to be restored:</u>
                </h3>
                <GetDetailUi
                  date={selectedVerDate}
                  author={selectedVerAuthor}
                  email={selectedVerEmail}
                />
              </>
            ) : (
              <>
                <h3>
                  <u className='text-secondary'>Version to be deleted:</u>
                </h3>
                <GetDetailUi
                  date={selectedVerDate}
                  author={selectedVerAuthor}
                  email={selectedVerEmail}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const onDeleteVersion = useCallback(() => {
    if (selectedVersionIndex === undefined) {
      dispatchUiNotification('Version Delete Failed',t('editor.modal.revision.errorVersionId'), {});
      return;
    }
    deleteVersion({
      entitySlug,
      versionId:
        versionsListData?.versions[selectedVersionIndex.newVerIndex!].attributes
          .version_id
    })
      .unwrap()
      .then((res) => {
        if (res.success) {
          setSelectedVersionIndex();
          dispatchUiNotification(
            'Version Delete Success',
            'Version Deleted Successfully',
            {}
          );
          if (versionsListData?.versionsCount <= 2) {
            onHide();
          }
        } else {
          dispatchUiNotification('Version Delete Failed',res.error, {});
        }
        setShowConfirmation({
          show: false,
          message: '',
          action: ''
        });
      })
      .catch((err) => {
        dispatchUiNotification('Version Delete Failed',err.data.error, {});
        setShowConfirmation({
          show: false,
          message: '',
          action: ''
        });
      });
  }, [
    entitySlug,
    deleteVersion,
    selectedVersionIndex,
    versionsListData,
    onHide,
    t,
    dispatchUiNotification
  ]);

  return (
    <>
      <ConfirmationModal
        show={showConfirmation.show}
        title={`${
          showConfirmation.action === 'delete' ? 'Delete' : 'Restore'
        } Confirmation`}
        onHide={() =>
          setShowConfirmation({
            show: false,
            message: '',
            action: ''
          })
        }
        onConfirm={() =>
          showConfirmation.action === 'restore'
            ? onRestoreToVersion()
            : onDeleteVersion()
        }
        message={showConfirmation.message}
      />
      <Modal.Footer className='d-flex justify-content-between'>
        <div>
          Versions: <Badge bg='info'>{versionsListData?.versionsCount}</Badge>
        </div>
        {selectedVersionIndex ? (
          <DropdownButton
            autoClose={false}
            placement='top'
            disabled={versionsListIsError || versionsListIsFetching}
            title={versionsListIsFetching ? <Loader /> : 'Previous Version'}>
            {versionsListData?.versions.map(
              (revision: VersionMetadata, index: number) => (
                <Dropdown.Item
                  onClick={() => handleOldVerSelect(index)}
                  active={selectedVersionIndex.oldVerIndex === index}
                  disabled={selectedVersionIndex.newVerIndex === index}
                  key={revision.id}>
                  {getFormattedTime(revision.attributes.created_at)}{' '}
                  {index === 0 ? ' (Current Version)' : null}
                </Dropdown.Item>
              )
            )}
          </DropdownButton>
        ) : (
          <div />
        )}
        <div className='d-flex gap-4'>
          {/* <Button variant='secondary' onClick={onHide}>
            <Trans i18nKey={'common.close'} />
          </Button> */}
          {selectedVersionIndex && selectedVersionIndex.newVerIndex !== 0 ? (
            <>
              {versionsListData?.versionsCount === 10 ? (
                <Button
                  variant='danger'
                  disabled={selectedVersionIndex === undefined}
                  onClick={() =>
                    setShowConfirmation({
                      show: true,
                      message: getConfirmationModalMessageUI('delete'),
                      action: 'delete'
                    })
                  }>
                  <Trans i18nKey={'editor.modal.revision.deleteButton'} />
                </Button>
              ) : (
                <Button
                  variant='info'
                  disabled={selectedVersionIndex === undefined}
                  onClick={() =>
                    setShowConfirmation({
                      show: true,
                      message: getConfirmationModalMessageUI('restore'),
                      action: 'restore'
                    })
                  }>
                  <Trans i18nKey={'editor.modal.revision.restoreButton'} />
                </Button>
              )}
            </>
          ) : null}
        </div>
      </Modal.Footer>
    </>
  );
};
